import { Link, toast } from "@heroui/react"
import { Button, InputGroup, Label, TextField } from "~/lib/heroui"
import { useState } from "react"
import { AuthShell } from "../components/AuthShell"
import { ConfirmationPending } from "../components/ConfirmationPending"
import { PhoneCodeField } from "../components/PhoneCodeField"
import { PasswordField } from "../components/PasswordField"
import { AuthRequestError, signUp, storeAuthToken } from "../lib/auth"

type SignUpPageProps = { redirectTo?: string }
type Mode = "email" | "phone"
type AwaitingConfirmation = { email: string; message: string }

export function SignUpPage({ redirectTo = "/dashboard" }: SignUpPageProps) {
  const [mode, setMode] = useState<Mode>("email")
  const [pending, setPending] = useState(false)
  const [awaiting, setAwaiting] = useState<AwaitingConfirmation>()

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setPending(true)
    const formData = new FormData(event.currentTarget)
    const phone = String(formData.get("phone_number") ?? "").trim()
    const email = String(formData.get("email") ?? "").trim()
    const password = String(formData.get("password") ?? "")
    const passwordConfirmation = String(
      formData.get("password_confirmation") ?? "",
    )
    if (password !== passwordConfirmation) {
      toast.danger("两次输入的密码不一致")
      setPending(false)
      return
    }

    try {
      const result = await signUp(
        mode === "phone"
          ? {
              identifier: { type: "phone", value: phone },
              verification_code: String(
                formData.get("verification_code") ?? "",
              ),
              password,
              password_confirmation: passwordConfirmation,
            }
          : {
              identifier: { type: "email", value: email },
              password,
              password_confirmation: passwordConfirmation,
            },
      )
      if (result.status === "pending_confirmation") {
        setAwaiting({ email: result.email, message: result.message })
        return
      }
      storeAuthToken(result.token)
      window.location.assign(redirectTo)
    } catch (err) {
      if (
        mode === "email" &&
        err instanceof AuthRequestError &&
        err.code === "email_unconfirmed"
      ) {
        setAwaiting({ email, message: err.message })
        return
      }
      toast.danger(err instanceof Error ? err.message : "注册失败，请稍后再试")
    } finally {
      setPending(false)
    }
  }

  if (awaiting)
    return (
      <AuthShell subtitle="最后一步，验证你的邮箱" title="查收确认邮件">
        <ConfirmationPending
          email={awaiting.email}
          message={awaiting.message}
        />
      </AuthShell>
    )

  return (
    <AuthShell subtitle="创建账号，开始记录孩子的成长" title="注册">
      <form className="auth-form flex flex-col gap-4 p-6" onSubmit={onSubmit}>
        <div className="auth-tabs" role="tablist" aria-label="注册方式">
          <button
            aria-selected={mode === "email"}
            className={mode === "email" ? "is-active" : ""}
            onClick={() => setMode("email")}
            role="tab"
            type="button"
          >
            邮箱注册
          </button>
          <button
            aria-selected={mode === "phone"}
            className={mode === "phone" ? "is-active" : ""}
            onClick={() => setMode("phone")}
            role="tab"
            type="button"
          >
            手机号码注册
          </button>
        </div>
        {mode === "email" ? (
          <>
            <TextField isRequired name="email" type="email">
              <Label>邮箱</Label>
              <InputGroup variant="secondary">
                <InputGroup.Input
                  autoComplete="email"
                  placeholder="you@example.com"
                />
              </InputGroup>
            </TextField>
            <PasswordField
              autoComplete="new-password"
              isRequired
              label="密码"
              minLength={8}
              name="password"
            />
            <PasswordField
              autoComplete="new-password"
              isRequired
              label="确认密码"
              minLength={8}
              name="password_confirmation"
            />
          </>
        ) : (
          <>
            <PhoneCodeField />
            <PasswordField
              autoComplete="new-password"
              isRequired
              label="密码"
              minLength={8}
              name="password"
            />
            <PasswordField
              autoComplete="new-password"
              isRequired
              label="确认密码"
              minLength={8}
              name="password_confirmation"
            />
          </>
        )}
        <Button fullWidth isPending={pending} type="submit">
          注册
        </Button>
        <p className="text-muted text-center text-sm">
          已有账号？{" "}
          <Link className="text-primary-ink font-medium" href="/login">
            登录
          </Link>
        </p>
      </form>
    </AuthShell>
  )
}
